<template>
  <div>
    <ontos-view-class-dialog v-if="target && target.kind === 'class'"
                             :idea-alias="target.idea" :workspace="workspace"
                             @closed="$emit('closed')"
                             @showMessage="$emit('showMessage', $event)"
                             @showErrorMessage="$emit('showErrorMessage', $event)"/>

    <!-- Só edita: a `property` daqui nunca vem vazia — o diagrama não tem gatilho de criar. -->
    <ontos-property-dialog v-if="property"
                           :idea="idea" :workspace="workspace" :property="property"
                           :saving="saving"
                           @save="$emit('save-property', $event)"
                           @closed="$emit('closed')"/>

    <ontos-workflow-dialog v-if="workflow"
                           :workflow="workflow" :workspace="workspace" :saving="saving"
                           @save="$emit('save-workflow', $event)"
                           @closed="$emit('closed')"/>

    <!-- Só o save: a caixa de domínio só existe no diagrama para um domínio que JÁ existe,
         então daqui nunca se cria — criar é o "+" da seção de domínios. -->
    <ontos-domain-dialog v-if="domain"
                         :domain="domain" :workspace="workspace" :saving="saving"
                         @save="$emit('save-domain', $event)"
                         @closed="$emit('closed')"/>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ViewClassDialog from '@/components/design/class/ViewClassDialog.vue'
import PropertyDialog from '@/components/design/property/PropertyDialog.vue'
import WorkflowDialog from '@/components/design/workflow/WorkflowDialog.vue'
import DomainDialog from '@/components/design/domain/DomainDialog.vue'
import {DiagramTarget} from '@/utils/classModel'
import {EnhancedWorkpace} from '@/types/Workspace'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import Domain from '@/types/Domain'

/**
 * Os destinos de um clique no diagrama, num componente só.
 *
 * Existe para poder ser montado em DOIS lugares: dentro do diálogo de tela cheia, quando
 * é de lá que o clique veio, e na raiz da tela quando não. O Vuetify descobre os diálogos
 * aninhados por provide/inject, então um modal aberto de dentro de outro precisa nascer na
 * subárvore dele — como irmão, o de baixo se acha o de cima e disputa o foco. É a mesma
 * regra que faz o InstanceEdit desenhar o diálogo de transferência ao lado do painel.
 *
 * Só a casca: quem guarda o estado e persiste é a tela. Assim os dois pontos de montagem
 * são duas linhas, e não duas cópias do markup.
 */
@Component({emits: ['closed', 'save-workflow', 'save-domain', 'save-property'],
  name: 'ontos-design-dialogs',
  components: {
    'ontos-view-class-dialog': ViewClassDialog,
    'ontos-property-dialog': PropertyDialog,
    'ontos-workflow-dialog': WorkflowDialog,
    'ontos-domain-dialog': DomainDialog
  }
})
class DesignDialogs extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /** O que foi clicado, ou null com tudo fechado */
  @Prop({default: null}) readonly target!: DiagramTarget | null
  @Prop({type: Boolean, default: false}) readonly saving!: boolean

  /** O domínio não vem de coleção nenhuma: é o único alvo sem `idea`. */
  get idea(): Idea | undefined {
    const target = this.target
    if (!target || target.kind === 'domain') return undefined
    return this.workspace.ideaWith(target.idea)
  }

  /** A propriedade clicada, como está no workspace — a janela trabalha numa cópia dela. */
  get property(): IdeaProperty | undefined {
    const target = this.target
    if (!target || target.kind !== 'property' || !this.idea) return undefined
    return this.idea.properties.find(candidate => candidate.alias === target.property)
  }

  get workflow() {
    if (!this.target || this.target.kind !== 'workflow' || !this.idea) return undefined
    return this.workspace.workflowOf(this.idea)
  }

  /**
   * O domínio sai do workspace, como a propriedade: é esse objeto que a tela recebe de
   * volta no `save` e é por ele que ela acha o que atualizar.
   *
   * O `domainWith` devolve a versão enriquecida, e o modal precisa do domínio cru — o
   * deepCopy do rascunho levaria os métodos junto para dentro do diff.
   */
  get domain(): Domain | undefined {
    const target = this.target
    if (!target || target.kind !== 'domain') return undefined
    return this.workspace.domains.find(candidate => candidate.alias === target.domain)
  }
}

export default toNative(DesignDialogs)
</script>
